import { Component, computed, forwardRef, inject, Injector, input, model, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { DxTextBoxModule } from 'devextreme-angular';
import { ValueChangedEvent } from 'devextreme/ui/text_box';
import { DxButtonTypes } from 'devextreme-angular/ui/button';
import { CdmSampleResult, TrackingService } from 'src/app/tracking/services/tracking.service';
import { GenericModalService, ModalSettingsTemplate } from 'src/app/shared/generic-modal/generic-modal.service';
import { XPathHelperDialogComponent } from '../../internal/xpath-helper-dialog/xpath-helper-dialog.component';
import { ApplicationInsightsService } from 'src/app/shared/insights/application-insights.service';
import { Layouts } from '../../../layouts';

@Component({
  selector: 'app-xpath',
  templateUrl: './xpath.component.html',
  styleUrl: './xpath.component.scss',
  imports: [Layouts, DxTextBoxModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => XPathComponent),
      multi: true
    }
  ]
})
export class XPathComponent implements OnInit, ControlValueAccessor {
  private injector = inject(Injector);
  private modalService = inject(GenericModalService);
  private trackingService = inject(TrackingService);
  private appInsights = inject(ApplicationInsightsService);

  value = model<string>('');
  readonly = input<boolean>(false);
  documentTypeId = input<number | null>(null);
  disabled = model(false);

  private ngControl = signal<NgControl>(null);
  private control = computed(() => this.ngControl()?.control as FormControl);

  private onChange: (value: unknown) => void = () => { };
  private onTouched: () => void = () => { };
  private isBusy = signal(false);

  pickerButton: DxButtonTypes.Properties = {
    text: 'XPath helper',
    stylingMode: 'contained',
    type: 'default',
    onClick: () => void this.openXPathHelperDialog(),
  };

  ngOnInit(): void {
    // It is necessary to get the NgControl like this (and not via inject())
    // because this component may be used without a form control and  injecting
    // via inject() may result in a cyclic dependency in DI.
    this.ngControl.set(this.injector.get(NgControl, null));
  }

  async openXPathHelperDialog(): Promise<void> {
    if (this.isBusy()) {
      return;
    }

    try {
      this.isBusy.set(true);

      let cdmSample: CdmSampleResult = null;
      if (this.documentTypeId()) {
        cdmSample = await this.trackingService.getCdmSampleForDocumentType(this.documentTypeId());
      }

      this.appInsights.logEvent('XPathComponent.XPathHelperOpened', { documentTypeId: this.documentTypeId() });

      try {
        const modalSettings = new ModalSettingsTemplate('lg', 'static');
        const xpath = await this.modalService.openComponentWithInitialize<string, XPathHelperDialogComponent>(XPathHelperDialogComponent, modalSettings, dialog => {
          if (cdmSample) {
            dialog.initialSampleData.set({
              fileName: cdmSample.FileName,
              fileSize: cdmSample.FileSize,
              timeStamp: cdmSample.TimeStamp,
              xml: cdmSample.Xml
            });
          }
          dialog.documentTypeId.set(this.documentTypeId());
          dialog.xpath.set(this.value());
        });
        if (xpath !== this.value()) {
          this.appInsights.logEvent('XPathComponent.XPathSelected', { documentTypeId: this.documentTypeId() });
          this.onChange(xpath);
          this.value.set(xpath);
        }
      }
      catch {
        this.appInsights.logEvent('XPathComponent.XPathHelperCanceled', { documentTypeId: this.documentTypeId() });
        // We get here if the dialog is dismissed (canceled)
      }
    }
    finally {
      this.isBusy.set(false);
    }
  }

  get isValid(): boolean {
    const fc = this.control();
    return !fc || (fc.invalid !== true) || (fc.pristine && fc.untouched);
  }

  onValueChanged(e: ValueChangedEvent): void {
    if (e.value !== this.value()) {
      this.appInsights.logEvent('XPathComponent.ValueChanged', { documentTypeId: this.documentTypeId() });
      this.onChange(e.value);
      this.value.set(e.value as string);
    }
  }

  onBlur(): void {
    this.onTouched();
  }

  /*
   * ControlValueAccessor interface methods
   */

  writeValue(obj: string): void {
    this.value.set(obj);
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(!!isDisabled);
  }
}
